// ============================================================================
// attachmentApi.js
// ----------------------------------------------------------------------------
// Helpers para cargar adjuntos como blobs autenticados (contrato §5).
// Los 3 endpoints nuevos (attachment_thumb, attachment_file, attachment_preview)
// requieren token Bearer; las URLs públicas legacy de `uploads/` quedan
// obsoletas y este módulo NO las usa.
//
// Ciclo de vida de un blob URL:
//   1. fetchBlobUrl() → crea el URL con URL.createObjectURL
//   2. El llamante lo usa (src de <img>, href de <iframe>, etc.)
//   3. El llamante llama revokeBlobUrl() cuando ya no lo necesita
//      (al desmontar el componente o al cambiar de adjunto en el lightbox)
// ============================================================================

import apiClient from './apiClient';

// ── Core ─────────────────────────────────────────────────────────────────────

/**
 * Fetch autenticado que devuelve un blob URL de createObjectURL.
 * El llamante DEBE liberar el URL con revokeBlobUrl cuando ya no lo necesite.
 *
 * @param {string} endpoint  Nombre del endpoint (attachment_thumb | attachment_file | attachment_preview)
 * @param {Object} params    Parámetros adicionales de query (sin `endpoint`)
 * @returns {Promise<string>} blob URL
 * @throws  {Error}          error.status = código HTTP si la respuesta no es 2xx
 */
export async function fetchBlobUrl(endpoint, params = {}) {
  // raw:true devuelve el Response sin parsear (necesitamos el blob). El apiClient
  // pone el Bearer y deriva la base por tenant; la sesión caducada (401/403 con
  // token) la gestiona globalmente. El resto de códigos (404/415/503) se
  // preservan como error con .status para que los llamantes discriminen.
  const response = await apiClient.get(endpoint, params, { raw: true });

  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Revoca un blob URL liberando la memoria del objeto.
 * Seguro de llamar con null / undefined / URL no-blob (no hace nada).
 *
 * @param {string|null|undefined} url
 */
export function revokeBlobUrl(url) {
  if (url && typeof url === 'string' && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

// ── Wrappers de los 3 endpoints ───────────────────────────────────────────────

/** Miniatura JPG cacheada (portada de imagen / PDF / Office).
 *  w = anchura en px; el backend lo acota al rango 80–640. */
export const fetchThumb = (id, w = 320) =>
  fetchBlobUrl('attachment_thumb', { id, w });

/** Fichero original. download=true → Content-Disposition: attachment. */
export const fetchFile = (id, download = false) =>
  fetchBlobUrl('attachment_file', download ? { id, download: 1 } : { id });

/** PDF listo para <iframe>: el fichero PDF original, o Office convertido a PDF
 *  en el backend con LibreOffice. Devuelve 503 si LibreOffice no está disponible
 *  y 415 si el tipo de fichero no admite preview (p.ej. "other"). */
export const fetchPreview = (id) =>
  fetchBlobUrl('attachment_preview', { id });

// ── Descarga autenticada ──────────────────────────────────────────────────────

/**
 * Descarga autenticada: fetch → blob → click en <a> → revoke con delay.
 * Sustituye al patrón `<a href>` directo, que ya no funciona con endpoints
 * protegidos por token.
 *
 * @param {number|string} id       ID del adjunto en archivos_adjuntos
 * @param {string}        filename Nombre sugerido para la descarga
 */
export async function downloadAttachment(id, filename) {
  const blobUrl = await fetchFile(id, true);
  try {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'archivo';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // Pequeño delay para que el navegador procese el clic antes de revocar
    setTimeout(() => revokeBlobUrl(blobUrl), 2000);
  }
}
